import React, { useState, useRef, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import JsBarcode from 'jsbarcode';
import './BarcodeGenerator.css';

function formatBatchValue(raw: any): string {
  if (raw == null) return '';

  if (raw instanceof Date && !isNaN(raw.getTime())) {
    const day = raw.getDate().toString().padStart(2, '0');
    const month = (raw.getMonth() + 1).toString().padStart(2, '0');
    const year = raw.getFullYear();
    return `${day}.${month}.${year}`;
  }

  if (typeof raw === 'number' && raw > 30000 && raw < 100000) {
    const excelEpoch = new Date(1899, 11, 30);
    const jsDate = new Date(excelEpoch.getTime() + (raw * 24 * 60 * 60 * 1000));
    const day = jsDate.getDate().toString().padStart(2, '0');
    const month = (jsDate.getMonth() + 1).toString().padStart(2, '0');
    const year = jsDate.getFullYear();
    return `${day}.${month}.${year}`;
  }

  return String(raw).trim();
}

function formatCellValue(raw: any): string {
  if (raw == null) return '';
  if (raw instanceof Date && !isNaN(raw.getTime())) {
    const day = raw.getDate().toString().padStart(2, '0');
    const month = (raw.getMonth() + 1).toString().padStart(2, '0');
    const year = raw.getFullYear();
    return `${day}.${month}.${year}`;
  }
  return String(raw);
}

interface BarcodeLabel {
  productNumber: string;
  batchNumber: string;
}

const BarcodeGenerator: React.FC = () => {
  const [view, setView] = useState<'upload' | 'barcodes'>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [allRows, setAllRows] = useState<any[][]>([]);
  const [productCol, setProductCol] = useState('');
  const [batchCol, setBatchCol] = useState('');
  const [quantities, setQuantities] = useState<Record<number, number>>({});
  const [labels, setLabels] = useState<BarcodeLabel[]>([]);
  const [error, setError] = useState('');

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError('');
    const selected = e.target.files?.[0];
    if (!selected) return;

    const ext = selected.name.toLowerCase();
    if (!ext.endsWith('.xlsx') && !ext.endsWith('.xls')) {
      setError('Моля, изберете Excel файл (.xlsx или .xls)');
      return;
    }

    if (selected.size > 50 * 1024 * 1024) {
      setError('Файлът е твърде голям. Максималният размер е 50MB');
      return;
    }

    setFile(selected);
    parseFile(selected);
  };

  const parseFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array', cellDates: true });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true }) as any[][];

        if (rows.length < 2) {
          setError('Файлът е празен или няма достатъчно редове');
          return;
        }

        const headerRow = rows[0].map((h: any) => String(h ?? '').trim());
        setHeaders(headerRow);
        setAllRows(rows.slice(1));
        setQuantities({});

        autoDetectColumns(headerRow);
      } catch {
        setError('Грешка при четене на файла. Уверете се, че е валиден Excel файл.');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const autoDetectColumns = (headerRow: string[]) => {
    const productPatterns = ['product', 'продукт', 'артикул', 'item', 'sku', 'номер на продукт', 'product number'];
    const batchPatterns = ['batch', 'партида', 'lot', 'batch number', 'номер на партида'];

    for (const h of headerRow) {
      const lower = h.toLowerCase();
      if (!productCol && productPatterns.some(p => lower.includes(p))) {
        setProductCol(h);
      }
      if (!batchCol && batchPatterns.some(p => lower.includes(p))) {
        setBatchCol(h);
      }
    }
  };

  const handleQuantityChange = (rowIndex: number, value: string) => {
    const num = parseInt(value, 10);
    setQuantities(prev => ({
      ...prev,
      [rowIndex]: isNaN(num) || num < 0 ? 0 : num,
    }));
  };

  const handleRemoveRow = (rowIndex: number) => {
    setAllRows(prev => prev.filter((_, i) => i !== rowIndex));
    setQuantities(prev => {
      const updated: Record<number, number> = {};
      for (const [key, val] of Object.entries(prev)) {
        const k = Number(key);
        if (k < rowIndex) updated[k] = val;
        else if (k > rowIndex) updated[k - 1] = val;
      }
      return updated;
    });
  };

  const totalLabels = Object.values(quantities).reduce((sum, q) => sum + q, 0);

  const handleGenerate = () => {
    if (!productCol || !batchCol) {
      setError('Моля, изберете колони за Продуктов номер и Партиден номер');
      return;
    }

    const productIdx = headers.indexOf(productCol);
    const batchIdx = headers.indexOf(batchCol);

    const missingRows: number[] = [];
    for (let i = 0; i < allRows.length; i++) {
      const productNumber = String(allRows[i][productIdx] ?? '').trim();
      if (productNumber && (!quantities[i] || quantities[i] <= 0)) {
        missingRows.push(i + 1);
      }
    }

    if (missingRows.length > 0) {
      setError(`Моля, въведете количество за всички продукти. Липсва количество на ред${missingRows.length === 1 ? '' : 'ове'}: ${missingRows.join(', ')}`);
      return;
    }

    const generatedLabels: BarcodeLabel[] = [];

    for (let i = 0; i < allRows.length; i++) {
      const row = allRows[i];
      const qty = quantities[i] || 0;
      if (qty <= 0) continue;

      const productNumber = String(row[productIdx] ?? '').trim();
      const batchNumber = formatBatchValue(row[batchIdx]);

      if (!productNumber) continue;

      for (let j = 0; j < qty; j++) {
        generatedLabels.push({ productNumber, batchNumber });
      }
    }

    if (generatedLabels.length === 0) {
      setError('Не бяха генерирани баркодове. Проверете данните и въведените количества.');
      return;
    }

    setLabels(generatedLabels);
    setError('');
    setView('barcodes');
  };

  const handleReset = () => {
    setView('upload');
    setFile(null);
    setHeaders([]);
    setAllRows([]);
    setProductCol('');
    setBatchCol('');
    setQuantities({});
    setLabels([]);
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (view === 'barcodes') {
    return (
      <div className="barcode-generator">
        <div className="barcode-actions no-print">
          <button className="barcode-btn barcode-btn-primary" onClick={handlePrint}>
            Принтирай ({labels.length} баркод{labels.length === 1 ? '' : 'а'})
          </button>
          <button className="barcode-btn barcode-btn-secondary" onClick={handleReset}>
            Нов файл
          </button>
        </div>

        <div className="barcode-grid">
          {labels.map((label, idx) => (
            <BarcodeCard key={idx} label={label} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="barcode-generator">
      <div className="barcode-header">
        <h2>Генератор на Баркодове</h2>
        <p className="barcode-description">
          Качете Excel файл с продуктови номера и партидни номера, след което задайте количество за всеки продукт.
        </p>
      </div>

      {error && (
        <div className="barcode-error">
          <p>{error}</p>
          <button onClick={() => setError('')}>&times;</button>
        </div>
      )}

      <div className="barcode-upload-section">
        <label className="barcode-file-label">
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileChange}
            className="barcode-file-input"
          />
          <span className="barcode-file-btn">
            {file ? file.name : 'Изберете Excel файл'}
          </span>
        </label>
      </div>

      {headers.length > 0 && (
        <>
          <div className="barcode-mapping-section">
            <h3>Съпоставяне на колони</h3>
            <div className="barcode-mapping-grid">
              <div className="barcode-mapping-field">
                <label>Продуктов номер</label>
                <select value={productCol} onChange={(e) => setProductCol(e.target.value)}>
                  <option value="">-- Изберете колона --</option>
                  {headers.map((h) => (
                    <option key={h} value={h}>{h}</option>
                  ))}
                </select>
              </div>
              <div className="barcode-mapping-field">
                <label>Партиден номер</label>
                <select value={batchCol} onChange={(e) => setBatchCol(e.target.value)}>
                  <option value="">-- Изберете колона --</option>
                  {headers.map((h) => (
                    <option key={h} value={h}>{h}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="barcode-preview-section">
            <h3>Данни и количества ({allRows.length} реда)</h3>
            <div className="barcode-preview-table-wrapper">
              <table className="barcode-preview-table">
                <thead>
                  <tr>
                    <th className="barcode-row-action-th"></th>
                    {headers.map((h, i) => (
                      <th key={i} className={
                        h === productCol ? 'col-highlight-product' :
                        h === batchCol ? 'col-highlight-batch' : ''
                      }>{h}</th>
                    ))}
                    <th className="col-highlight-quantity">Количество</th>
                  </tr>
                </thead>
                <tbody>
                  {allRows.map((row, ri) => (
                    <tr key={ri}>
                      <td className="barcode-row-action-cell">
                        <button
                          className="barcode-row-delete"
                          title="Премахни ред"
                          onClick={() => handleRemoveRow(ri)}
                        >&times;</button>
                      </td>
                      {headers.map((h, ci) => (
                        <td key={ci} className={
                          h === productCol ? 'col-highlight-product' :
                          h === batchCol ? 'col-highlight-batch' : ''
                        }>{formatCellValue(row[ci])}</td>
                      ))}
                      <td className="col-highlight-quantity barcode-qty-cell">
                        <input
                          type="number"
                          min="0"
                          value={quantities[ri] || ''}
                          placeholder="0"
                          onChange={(e) => handleQuantityChange(ri, e.target.value)}
                          className="barcode-qty-input"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="barcode-generate-section">
            <button
              className="barcode-btn barcode-btn-primary barcode-btn-large"
              onClick={handleGenerate}
              disabled={!productCol || !batchCol}
            >
              Генерирай баркодове{totalLabels > 0 ? ` (${totalLabels})` : ''}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const BarcodeCard: React.FC<{ label: BarcodeLabel }> = ({ label }) => {
  const svgRef = useRef<SVGSVGElement>(null);

  const renderBarcode = useCallback(() => {
    if (svgRef.current) {
      try {
        JsBarcode(svgRef.current, label.productNumber, {
          format: 'CODE128',
          width: 2,
          height: 90,
          displayValue: false,
          margin: 0,
          flat: true,
        });
      } catch {
        if (svgRef.current) {
          svgRef.current.innerHTML = '<text x="50%" y="50%" text-anchor="middle" fill="red" font-size="10">Грешка</text>';
        }
      }
    }
  }, [label.productNumber]);

  useEffect(() => {
    renderBarcode();
  }, [renderBarcode]);

  return (
    <div className="barcode-card">
      <div className="barcode-card-inner">
        <div className="barcode-card-company">Белла 99 ЕООД</div>
        <div className="barcode-card-batch">{label.batchNumber}</div>
        <div className="barcode-card-product">{label.productNumber}</div>
        <svg ref={svgRef} className="barcode-card-svg"></svg>
      </div>
    </div>
  );
};

export default BarcodeGenerator;
